import { Alert, CircularProgress, Grid } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { LayoutModule } from '../../layout/modules';
import LoadingButtonGeneral from '../../components/general/buttons/loadingButtonGeneral';
import { ModulesMessages } from '../../../shared/messages/modulesMessages';
import { ORBIT_HISTORY } from '../../../routes/paths';
import { OrbitFormSection } from './components/orbitFormSection';
import { OrbitRadicationSuccessDialog } from './components/radicationSuccessDialog';
import { OrbitAddress } from './address';
import { OrbitCallInfo } from './callInfo';
import { OrbitChannelForm } from './channelForm';
import { OrbitDefensorFields } from './channelForm/defensorFields';
import { isOrbitDefensorConsumidor } from './channelForm/formData';
import { OrbitCitizenInfo } from './citizenInfo';
import { OrbitPqrInfo } from './pqrInfo';
import { OrbitPrivacyConsent } from './privacyConsent';
import { OrbitSpecialCondition } from './specialCondition';
import { useOrbitBpParams } from './hooks/useOrbitBpParams';
import { useOrbitCaseDetailLoader } from './hooks/useOrbitCaseDetailLoader';
import { useOrbitRadicationCatalogs } from './hooks/useOrbitRadicationCatalogs';
import { useOrbitRadicationForm } from './hook';

export const OrbitRadication = () => {
    const navigate = useNavigate();
    const { caseId } = useParams();
    const readOnly = Boolean(caseId);

    const {
        loading,
        successOpen,
        filingNumber,
        attachmentsResetKey,
        handleSubmit,
        handleClean,
        handleNewRequest,
        handleRefreshPage,
        formikOrbitChannel,
        setFormikOrbitChannel,
        setFormikCallInfo,
        setFormikCitizenInfo,
        setFormikAddress,
        setFormikSpecialCondition,
        setFormikPqrInfo,
        setFormikPrivacyConsent,
        lookingUpAccount,
        lookupAccountByDocument,
        catalogsReady,
    } = useOrbitRadicationForm();

    const bpParams = useOrbitBpParams();
    const { loadingCase, caseValues } = useOrbitCaseDetailLoader(caseId);
    const { loading: loadingCatalogs, error: catalogsError } =
        useOrbitRadicationCatalogs(!readOnly);

    const pageLoading = loading || loadingCase || loadingCatalogs;
    const sectionProps = { readOnly };
    const showForm = readOnly
        ? !loadingCase && Boolean(caseValues)
        : !loadingCatalogs && !catalogsError;
    const showPageLoader =
        (readOnly && loadingCase) || (!readOnly && loadingCatalogs);

    return (
        <LayoutModule
            title={
                readOnly
                    ? ModulesMessages.ORBIT_DETAIL_PAGE
                    : ModulesMessages.ORBIT_RADICATION_PAGE
            }
            showTitle
            withAccordion={false}
        >
            {showPageLoader && (
                <Grid container justifyContent="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Grid>
            )}
            {!readOnly && catalogsError && !loadingCatalogs && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {catalogsError}
                </Alert>
            )}
            {showForm && (
                <>
                    {!readOnly && (
                        <OrbitFormSection first>
                            <OrbitCallInfo
                                seededCallId={bpParams.callId}
                                callIdReadOnly={bpParams.hasCallIdFromUrl}
                                onChangeFormik={(formik) =>
                                    setFormikCallInfo(formik)
                                }
                            />
                        </OrbitFormSection>
                    )}
                    <OrbitFormSection first={readOnly} readOnly={readOnly}>
                        <OrbitChannelForm
                            {...sectionProps}
                            seedValues={caseValues?.channel}
                            seedChannel={
                                readOnly ? undefined : bpParams.orbitChannel
                            }
                            onChangeFormik={(formik) =>
                                setFormikOrbitChannel(formik)
                            }
                        />
                    </OrbitFormSection>
                    <OrbitFormSection readOnly={readOnly}>
                        <OrbitCitizenInfo
                            {...sectionProps}
                            seedValues={caseValues?.citizen}
                            seededDocumentType={
                                readOnly ? undefined : bpParams.documentType
                            }
                            seededDocument={
                                readOnly ? undefined : bpParams.document
                            }
                            lookingUpAccount={lookingUpAccount}
                            accountLookupCatalogsReady={catalogsReady}
                            onAccountLookup={lookupAccountByDocument}
                            onChangeFormik={(formik) =>
                                setFormikCitizenInfo(formik)
                            }
                        />
                    </OrbitFormSection>
                    <OrbitFormSection readOnly={readOnly}>
                        <OrbitAddress
                            {...sectionProps}
                            seedValues={caseValues?.address}
                            onChangeFormik={(formik) =>
                                setFormikAddress(formik)
                            }
                        />
                    </OrbitFormSection>
                    <OrbitFormSection readOnly={readOnly}>
                        <OrbitSpecialCondition
                            {...sectionProps}
                            seedValues={caseValues?.specialCondition}
                            onChangeFormik={(formik) =>
                                setFormikSpecialCondition(formik)
                            }
                        />
                    </OrbitFormSection>
                    <OrbitFormSection readOnly={readOnly}>
                        <OrbitPqrInfo
                            {...sectionProps}
                            attachmentsResetKey={attachmentsResetKey}
                            seedValues={caseValues?.pqrInfo}
                            onChangeFormik={(formik) =>
                                setFormikPqrInfo(formik)
                            }
                        />
                    </OrbitFormSection>
                    {formikOrbitChannel &&
                        isOrbitDefensorConsumidor(
                            formikOrbitChannel.values.orbitChannel
                        ) && (
                        <OrbitFormSection readOnly={readOnly}>
                            <Grid
                                container
                                spacing={{ xs: 1.5, md: 1.5 }}
                                columnSpacing={{
                                    xs: 1.5,
                                    sm: 1.5,
                                    md: 1.5,
                                }}
                                pt="20px"
                            >
                                <OrbitDefensorFields
                                    formik={formikOrbitChannel}
                                    readOnly={readOnly}
                                    section="classification"
                                />
                            </Grid>
                        </OrbitFormSection>
                    )}
                    <OrbitFormSection readOnly={readOnly}>
                        <OrbitPrivacyConsent
                            {...sectionProps}
                            seedValues={caseValues?.privacyConsent}
                            seededAcceptTerms={
                                readOnly || !bpParams.hasHabeasDataFromUrl
                                    ? undefined
                                    : bpParams.habeasData
                            }
                            onChangeFormik={(formik) =>
                                setFormikPrivacyConsent(formik)
                            }
                        />
                    </OrbitFormSection>
                </>
            )}
            {showForm && (
                <Grid
                    container
                    spacing={{ xs: 1.5, md: 1.5 }}
                    columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                    pt={
                        isOrbitDefensorConsumidor(
                            formikOrbitChannel?.values.orbitChannel
                        )
                            ? 3
                            : 0
                    }
                >
                    {formikOrbitChannel &&
                        isOrbitDefensorConsumidor(
                            formikOrbitChannel.values.orbitChannel
                        ) && (
                        <Grid item xs={12}>
                            <OrbitFormSection>
                                <Grid
                                    container
                                    spacing={{ xs: 1.5, md: 1.5 }}
                                    columnSpacing={{
                                        xs: 1.5,
                                        sm: 1.5,
                                        md: 1.5,
                                    }}
                                >
                                    <OrbitDefensorFields
                                        formik={formikOrbitChannel}
                                        readOnly={readOnly}
                                        section="correspondence"
                                    />
                                </Grid>
                            </OrbitFormSection>
                        </Grid>
                    )}
                    <Grid
                        item
                        xs={12}
                        mt={
                            isOrbitDefensorConsumidor(
                                formikOrbitChannel?.values.orbitChannel
                            )
                                ? 2
                                : 5
                        }
                        mb={10}
                    >
                        {readOnly ? (
                            <LoadingButtonGeneral
                                title="Volver"
                                variant="outlined"
                                onClick={() => navigate(ORBIT_HISTORY)}
                            />
                        ) : (
                            <>
                                <LoadingButtonGeneral
                                    disabled={pageLoading}
                                    loading={pageLoading}
                                    title="Guardar"
                                    sx={{ mr: 1 }}
                                    onClick={handleSubmit}
                                />
                                <LoadingButtonGeneral
                                    disabled={pageLoading}
                                    title="Limpiar"
                                    variant="outlined"
                                    onClick={handleClean}
                                />
                            </>
                        )}
                    </Grid>
                </Grid>
            )}
            {!readOnly && (
                <OrbitRadicationSuccessDialog
                    open={successOpen}
                    filingNumber={filingNumber}
                    onNewRequest={handleNewRequest}
                    onRefreshPage={handleRefreshPage}
                />
            )}
        </LayoutModule>
    );
};

export { OrbitHistory } from './history';
